import * as Sentry from "@sentry/react"
import React, { useEffect } from "react"
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  Outlet,
  ScrollRestoration,
  useLocation,
  useNavigationType,
} from "react-router-dom"
import ErrorBoundary from "@/components/error-boundary/error-boundary.tsx"
import LandscapeBlock from "@/components/under-construction/landscape-block.tsx"
import { sentryConfig } from "@/config/sentry/sentry-config.ts"
import { getAssistantRoutes } from "@/features/assistant/routes/assistant-routes"
import { getApiaryRoutes } from "@/features/bees/routes/apiary-routes.tsx"
import { getHivesRoutes } from "@/features/bees/routes/hive-routes.tsx"
import { getHiveScalesRoutes } from "@/features/bees/routes/hive-scale-routes.tsx"
import { getQueensRoutes } from "@/features/bees/routes/queen-routes.tsx"
import { getBirdsGroupsRoutes } from "@/features/birds-groups/routes/birds-groups-routes.tsx"
import { getCropPlantingCalendarRoutes } from "@/features/crop-planting-calendar/routes/crop-planting-calendar-routes.tsx"
import {
  getCropPlantingRoutes,
  getCropRoutes,
} from "@/features/crops/routes/crops-routes.tsx"
import { getAnimalEventRoutes } from "@/features/events/animal-events/routes/animal-event-routes.tsx"
import { getApiaryEventRoutes } from "@/features/events/apiary-events/routes/apiary-event-routes.tsx"
import { getBirdsGroupsEventsRoutes } from "@/features/events/birds-groups-events/routes/birds-group-event-routes.tsx"
import { getCommonEventRoutes } from "@/features/events/common/routes/common-event-routes.tsx"
import { getCropPlantingEventsRoutes } from "@/features/events/crop-planting-events/routes/crop-planting-event-routes.tsx"
import { getHiveEventRoutes } from "@/features/events/hive-events/routes/hive-event-routes.tsx"
import { getFarmRoutes } from "@/features/farm/routes/farm-routes.tsx"
import { getFinanceRoutes } from "@/features/finance/routes/finance-routes.tsx"
import { getGestationalCalendarRoutes } from "@/features/gestational-calendar/routes/gestational-calendar-routes.tsx"
import { getHomeRoutes } from "@/features/home/routes/home-routes"
import { getMenuRoutes } from "@/features/menu/routes/menu-routes.tsx"
import ModalPortal from "@/features/modals/modal-portal/modal-portal"
import { getNoteRoutes } from "@/features/notes/routes/note-routes"
import { getNotificationRoutes } from "@/features/notifications/routes/notification-routes.tsx"
import { getParcelRoutes } from "@/features/parcels/routes/parcel-routes.tsx"
import { PARTNERS_ROUTES } from "@/features/partners/routes/partener-routes.tsx"
import { UNAUTHENTICATED_ROUTES } from "@/features/register-login/routes/register-login-routes.tsx"
import { getReportsRoutes } from "@/features/reports/routes/reports-routes.tsx"
import { getSettingsRoutes } from "@/features/settings-account/routes/settings-routes"
import { getTasksRoutes } from "@/features/tasks/routes/task-routes"
import {
  AIAssistantEnabled,
  AuthenticatedRoutes,
  BeeModuleEnabled,
  BirdsModuleEnabled,
  FarmModuleEnabled,
  FinanceJournalEnabled,
  GestationalCalendarEnabled,
  ParcelsModuleEnabled,
} from "@/routing/permission-guards.tsx"
import ToastConfig from "@/utils/toast-config/toasts-config"
const FarmPermissionsLoaded = React.lazy(
  () => import("@/routing/farm-permissions-loaded.tsx"),
)

Sentry.init({
  dsn: sentryConfig.dsn,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration({
      maskAllText: false,
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: sentryConfig.tracesSampleRate,
  tracePropagationTargets: [import.meta.env.VITE_API_URL],
  // Session Replay
  replaysSessionSampleRate: sentryConfig.replaysSessionSampleRate,
  replaysOnErrorSampleRate: sentryConfig.replaysOnErrorSampleRate,
  enabled: import.meta.env.PROD,
  environment: import.meta.env.MODE,
})

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouter(createBrowserRouter)

function GlobalOutlet() {
  return (
    <>
      <ScrollRestoration />
      <ModalPortal />
      <ToastConfig />
      <LandscapeBlock />
      <Outlet />
    </>
  )
}

const getDefaultRoutes = (isDesktop: boolean) => [
  ...getHomeRoutes(isDesktop),
  ...getSettingsRoutes(isDesktop),
  ...getNoteRoutes(isDesktop),
  ...getTasksRoutes(isDesktop),
  ...getReportsRoutes(isDesktop),
  ...PARTNERS_ROUTES,
  ...getCommonEventRoutes(isDesktop),
  ...getNotificationRoutes(isDesktop),
  ...getMenuRoutes(isDesktop),
]

const getAdditionalFeatureRoutes = (isDesktop: boolean) => [
  {
    element: <BeeModuleEnabled />,
    children: [
      ...getApiaryRoutes(isDesktop),
      ...getHivesRoutes(isDesktop),
      ...getQueensRoutes(isDesktop),
      ...getApiaryEventRoutes(isDesktop),
      ...getHiveEventRoutes(isDesktop),
      ...getHiveScalesRoutes(isDesktop),
    ],
  },
  {
    element: <AIAssistantEnabled />,
    children: [...getAssistantRoutes(isDesktop)],
  },
  {
    element: <GestationalCalendarEnabled />,
    children: [...getGestationalCalendarRoutes(isDesktop)],
  },
  {
    element: <BirdsModuleEnabled />,
    children: [
      ...getBirdsGroupsRoutes(isDesktop),
      ...getBirdsGroupsEventsRoutes(isDesktop),
    ],
  },
  {
    element: <ParcelsModuleEnabled />,
    children: [
      ...getParcelRoutes(isDesktop),
      ...getCropPlantingEventsRoutes(isDesktop),
      ...getCropPlantingCalendarRoutes(isDesktop),
      ...getCropPlantingRoutes(isDesktop),
      ...getCropRoutes(isDesktop),
    ],
  },
  {
    element: <FinanceJournalEnabled />,
    children: [...getFinanceRoutes(isDesktop)],
  },
  {
    element: <FarmModuleEnabled />,
    children: [...getFarmRoutes(isDesktop), ...getAnimalEventRoutes(isDesktop)],
  },
]

export const routerConfig = (isDesktop: boolean) => {
  return sentryCreateBrowserRouter(
    [
      {
        path: "/",
        element: <GlobalOutlet />,
        errorElement: (
          <Sentry.ErrorBoundary fallback={<ErrorBoundary />}>
            <ErrorBoundary />
          </Sentry.ErrorBoundary>
        ),
        children: [
          {
            element: <AuthenticatedRoutes />,
            children: [
              {
                path: "/",
                element: <FarmPermissionsLoaded />,
                children: [
                  ...getDefaultRoutes(isDesktop),
                  ...getAdditionalFeatureRoutes(isDesktop),
                ],
              },
            ],
          },
          UNAUTHENTICATED_ROUTES,
        ],
      },
    ],
    {},
  )
}
